import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Printer, Plus, X, ArrowRightLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fmtDate, money } from "../../lib/api";
import { printRecord } from "./print";
import Attachments from "./Attachments";

function LineItems({ value, onChange, disabled, priced }) {
  const lines = value || [];
  const set = (i, k, v) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const sub = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  return (
    <div className="border border-slate-200 rounded-md" data-testid="line-items">
      <div className="hidden sm:grid grid-cols-12 gap-2 px-2 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="col-span-2">Part no.</span><span className={priced ? "col-span-4" : "col-span-7"}>Description</span><span className="col-span-2">Qty</span>{priced && <span className="col-span-3">Unit price (ex GST)</span>}
      </div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 p-2 border-b border-slate-100">
          <input disabled={disabled} value={l.part_no || ""} onChange={(e) => set(i, "part_no", e.target.value)} placeholder="Part no." className="field col-span-4 sm:col-span-2" data-testid={`line-part-${i}`} />
          <input disabled={disabled} value={l.description} onChange={(e) => set(i, "description", e.target.value)} placeholder="Description" className={`field col-span-8 ${priced ? "sm:col-span-4" : "sm:col-span-7"}`} data-testid={`line-desc-${i}`} />
          <input disabled={disabled} type="number" value={l.qty} onChange={(e) => set(i, "qty", e.target.value)} placeholder="Qty" className="field col-span-3 sm:col-span-2 px-2" data-testid={`line-qty-${i}`} />
          {priced && <input disabled={disabled} type="number" step="0.01" value={l.unit_price} onChange={(e) => set(i, "unit_price", e.target.value)} placeholder="Unit $" className="field col-span-7 sm:col-span-3" data-testid={`line-price-${i}`} />}
          {!disabled && <button type="button" onClick={() => onChange(lines.filter((_, j) => j !== i))} className={`text-slate-400 hover:text-red-600 ${priced ? "col-span-2 sm:col-span-1" : "col-span-1"}`} aria-label="Remove line"><X className="w-4 h-4 mx-auto" /></button>}
        </div>
      ))}
      <div className="flex flex-wrap gap-2 items-center justify-between p-3 text-sm">
        {!disabled ? <button type="button" onClick={() => onChange([...lines, { part_no: "", description: "", qty: 1, unit_price: 0 }])} className="inline-flex items-center gap-1 font-semibold text-sky-700" data-testid="line-add-btn"><Plus className="w-4 h-4" />Add line</button> : <span />}
        {priced && <span className="font-mono text-slate-700" data-testid="line-totals">Subtotal {money(sub)} · GST {money(sub * 0.1)} · Total {money(sub * 1.1)}</span>}
      </div>
    </div>
  );
}

const CONVERT = {
  quotes: [["jobs", "Convert to Job"], ["invoices", "Create Invoice"]],
  jobs: [["invoices", "Create Invoice"], ["packing_slips", "Create Packing Slip"]],
  rfqs: [["purchase_orders", "Create Purchase Order"]],
};

function Input({ f, value, onChange, disabled, options }) {
  const common = { disabled, value: value ?? "", onChange: (e) => onChange(e.target.value), className: "field mt-1.5", "data-testid": `field-${f.key}` };
  if (f.type === "textarea") return <textarea rows={3} {...common} />;
  if (f.type === "select") return <select {...common}><option value="">—</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select>;
  if (f.type === "ref") return <select {...common}><option value="">—</option>{(options || []).map((o) => <option key={o.id} value={o.id}>{o.ref} · {o.title}</option>)}</select>;
  if (f.type === "date") return <input type="date" {...common} />;
  if (f.type === "number") return <input type="number" step="any" {...common} />;
  return <input {...common} />;
}

export default function RecordDialog({ mod, initial, writable, role, onClose, onSaved }) {
  const { session } = useAuth();
  const nav = useNavigate();
  const [rec, setRec] = useState(initial.id ? null : {});
  const [form, setForm] = useState(initial.id ? {} : initial.defaults || {});
  const [opts, setOpts] = useState({});
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;

  useEffect(() => {
    if (!initial.id) {
      return;
    }
    api.get(`/records/${mod.key}/${initial.id}`).then((r) => { setRec(r.data); setForm(r.data); }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [initial.id, mod.key, onClose]);

  useEffect(() => {
    mod.fields.filter((f) => f.type === "ref").forEach((f) => {
      api.get(`/records/${f.ref}/options`).then((r) => setOpts((o) => ({ ...o, [f.ref]: r.data })));
    });
  }, [mod]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {};
    mod.fields.forEach((f) => { if (f.type !== "computed" && form[f.key] !== undefined) payload[f.key] = form[f.key]; });
    try {
      if (isNew) await api.post(`/records/${mod.key}`, payload);
      else await api.put(`/records/${mod.key}/${initial.id}`, payload);
      toast.success(isNew ? "Record created" : "Record saved");
      onSaved();
      onClose();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };

  const convert = async (target) => {
    try {
      const { data } = await api.post(`/records/${mod.key}/${initial.id}/convert/${target}`);
      toast.success(`${data.ref} created`);
      onClose();
      nav(`/app/m/${target}?open=${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/records/${mod.key}/${initial.id}`);
      toast.success("Record deleted");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isNew ? `New — ${mod.name}` : `${rec?.ref || ""} ${rec?.title || ""}`}</DialogTitle>
          {!writable && <p className="text-sm text-amber-700" data-testid="record-readonly-note">Read-only — changes are disabled.</p>}
        </DialogHeader>
        {rec === null ? <p className="text-sm text-slate-500 py-8">Loading…</p> : (
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {mod.fields.map((f) => {
                const wide = ["textarea", "lines"].includes(f.type) || f.key === "title";
                if (f.type === "computed") {
                  if (isNew) return null;
                  return <div key={f.key}><span className="text-sm font-medium text-slate-700">{f.label}</span><p className="mt-2 font-mono text-sm" data-testid={`computed-${f.key}`}>{["total", "balance"].includes(f.key) ? money(rec[f.key]) : rec[f.key] ?? "—"}</p></div>;
                }
                return (
                  <label key={f.key} className={`block ${wide ? "sm:col-span-2" : ""}`}>
                    <span className="text-sm font-medium text-slate-700">{f.label}{f.required && <span className="text-red-600"> *</span>}</span>
                    {f.type === "lines"
                      ? <div className="mt-1.5"><LineItems value={form.lines} priced={f.priced} onChange={(v) => setForm({ ...form, lines: v })} disabled={!writable} /></div>
                      : <Input f={f} value={form[f.key]} options={opts[f.ref]} disabled={!writable} onChange={(v) => setForm({ ...form, [f.key]: v })} />}
                  </label>
                );
              })}
            </div>
            {!isNew && <Attachments module={mod.key} recordId={initial.id} writable={writable} canDelete={["org_admin", "manager", "consultant"].includes(role)} />}
            {!isNew && rec.history?.length > 0 && (
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold mb-2">History</p>
                <ul className="space-y-1 text-xs text-slate-600 max-h-32 overflow-y-auto" data-testid="record-history">
                  {[...rec.history].reverse().map((h, i) => <li key={i}>{fmtDate(h.ts)} — {h.by} {h.action}{h.fields?.length ? `: ${h.fields.join(", ")}` : ""}</li>)}
                </ul>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-end border-t border-slate-200 pt-4">
              {!isNew && <button type="button" onClick={() => printRecord(mod, rec, opts, session.org)} className="mr-auto inline-flex items-center gap-2 border border-slate-300 px-4 py-2 rounded-md text-sm font-semibold" data-testid="record-print-btn"><Printer className="w-4 h-4" />Print / PDF</button>}
              {!isNew && writable && (CONVERT[mod.key] || []).map(([t, l]) => <button key={t} type="button" onClick={() => convert(t)} className="inline-flex items-center gap-2 border border-sky-300 text-sky-800 hover:bg-sky-50 px-4 py-2 rounded-md text-sm font-semibold" data-testid={`record-convert-${t}`}><ArrowRightLeft className="w-4 h-4" />{l}</button>)}
              {!isNew && writable && ["org_admin", "manager", "consultant"].includes(role) && <button type="button" onClick={remove} className="inline-flex items-center gap-2 text-red-700 border border-red-200 hover:bg-red-50 px-4 py-2 rounded-md text-sm font-semibold" data-testid="record-delete-btn"><Trash2 className="w-4 h-4" />Delete</button>}
              <button type="button" onClick={onClose} className="border border-slate-300 px-4 py-2 rounded-md text-sm font-semibold" data-testid="record-cancel-btn">Close</button>
              {writable && <button disabled={busy} className="bg-sky-600 hover:bg-sky-700 text-white px-5 py-2 rounded-md text-sm font-semibold" data-testid="record-save-btn">{busy ? "Saving…" : "Save"}</button>}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
